import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { Figure, QuantileCells, ibamaCasesHref } from '@/pages/ibama/OverviewPage';
import { fetchIbamaCollection } from '@/services/ibama';
import type { AnswerOut, IbamaOutcomeTable } from '@/services/types';

/**
 * What became of IBAMA's debts.
 *
 * The collection outcome is the state of a DEBT, mostly recorded at
 * closure; SETTLED is an extinguished debt, not an upheld auto, and no share
 * here is a payment rate. Valor Pago is positive on every PENDING row, so
 * the recovery ratio is refused with that measurement and the amount is
 * never summed. The instalment ledger is per parcel; a plan is a case with
 * more than one row.
 */
export function IbamaCollectionPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'collection'],
    queryFn: fetchIbamaCollection,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:collection.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:collection.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ibama:collection.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ibama/export/collection" label={t('ibama:collection.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel
          answer={{ ...data.mix, value: data.mix.value?.outcomes ?? null }}
          titleKey="ibama:collection.mix.title"
          helpKey="ibama:collection.mix.help"
          labelPrefix="ibama:overview.outcomes."
          rowHref={(code) => ibamaCasesHref({ outcome: code })}
        />
        <RefusalCard title={t('ibama:collection.recovery.title')} answer={data.recovery} />
      </div>

      <FieldGuide i18nKey="ibama:collection.guide" level={2} />

      <OutcomeTable answer={data.by_nature} />
      <OutcomeTable answer={data.by_uf} />

      <section className="rounded-lg border bg-card p-4" data-plans>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ibama:collection.plans.title')}
        </h2>
        <Answer answer={data.plans} help={t('ibama:collection.plans.help')}>
          {(p) => (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure
                  label={t('ibama:collection.plans.singlePayment')}
                  value={number.format(p.single_payment_cases)}
                />
                <Figure
                  label={t('ibama:collection.plans.planCases')}
                  value={`${number.format(p.plan_cases)} (${percent.format(p.plan_share)})`}
                  href={ibamaCasesHref({ outcome: 'INSTALMENTS' })}
                />
                <Figure
                  label={t('ibama:collection.plans.rows')}
                  value={`${number.format(p.rows)} / ${number.format(p.rows_paid)}`}
                />
                <Figure
                  label={t('ibama:collection.plans.atMax')}
                  value={`${number.format(p.cases_at_max_size)} (${number.format(p.max_plan_size)})`}
                />
              </div>
              <div>
                <p className="mb-1 text-xs text-muted-foreground">
                  {t('ibama:collection.plans.rowsPerPlan')}
                </p>
                <QuantileCells summary={p.rows_per_plan} format={(v) => number.format(v)} />
              </div>
            </div>
          )}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-plan-sizes>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ibama:collection.planSizes.title')}
        </h2>
        <Answer answer={data.plan_sizes} help={t('ibama:collection.planSizes.help')}>
          {(rows) => {
            const max = Math.max(1, ...rows.map((r) => r.cases));
            return (
              <ol className="grid grid-cols-6 gap-1 sm:grid-cols-10 lg:grid-cols-12">
                {rows.map((r) => (
                  <li
                    key={r.size}
                    className="rounded-md border bg-muted/30 p-1 text-center"
                    data-size={r.size}
                    title={`${r.size}: ${number.format(r.cases)}`}
                  >
                    <div
                      className="mx-auto w-2 rounded-sm bg-primary/60"
                      style={{ height: `${Math.max(2, Math.round((r.cases / max) * 40))}px` }}
                    />
                    <div className="text-[10px] tabular-nums text-muted-foreground">{r.size}</div>
                    <div className="text-xs tabular-nums text-foreground">
                      {number.format(r.cases)}
                    </div>
                  </li>
                ))}
              </ol>
            );
          }}
        </Answer>
      </section>
    </div>
  );
}

function OutcomeTable({ answer }: { answer: AnswerOut<IbamaOutcomeTable> }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const kind = answer.value?.kind ?? 'nature';
  const href = (group: string, outcome?: string) =>
    kind === 'nature'
      ? ibamaCasesHref({ nature: group, outcome })
      : ibamaCasesHref({ uf: group, outcome });
  const outcome = (code: string) => t(`ibama:overview.outcomes.${code}`, { defaultValue: code });

  return (
    <section className="rounded-lg border bg-card p-4" data-outcomes={kind}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`ibama:collection.groups.${kind}.title`)}
      </h2>
      <Answer answer={answer} help={t(`ibama:collection.groups.${kind}.help`)}>
        {(table) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ibama:collection.groups.group')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:collection.groups.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:collection.groups.settled')}
                    </th>
                    <th className="px-3 py-2 font-medium">
                      {t('ibama:collection.groups.outcomes')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => (
                    <tr key={row.group} className="border-t align-top" data-group={row.group}>
                      <td className="px-3 py-2 text-foreground">{row.group}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <Link
                          to={href(row.group)}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {number.format(row.cases)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {percent.format(row.settled_share)}
                      </td>
                      <td className="px-3 py-2">
                        <ul className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                          {row.outcomes
                            .filter((o) => o.count > 0)
                            .map((o) => (
                              <li key={o.code} className="whitespace-nowrap" data-outcome={o.code}>
                                <Link
                                  to={href(row.group, o.code)}
                                  className="text-primary underline-offset-2 hover:underline"
                                >
                                  {outcome(o.code)}
                                </Link>
                                <span className="ml-1 tabular-nums text-muted-foreground">
                                  {number.format(o.count)} ({percent.format(o.share)})
                                </span>
                              </li>
                            ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {table.suppressed > 0 ? (
              <p className="text-xs text-muted-foreground" data-suppressed>
                {t('ibama:collection.groups.suppressed', {
                  groups: number.format(table.suppressed),
                  cases: number.format(table.suppressed_cases),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}
